/**
 * Gateway Store
 * Centralized state management for Gateway service status and health
 */

import { createStore } from 'zustand/vanilla';
import { devtools } from 'zustand/middleware';
import type {
  HealthState,
  ConnectionState,
  PollStatusInfo,
} from '../core/gateway/types';

interface GatewayState {
  // State
  health: HealthState;
  connection: ConnectionState;
  isPolling: boolean;
  lastPollTime: number | null;
  lastPollStatus: PollStatusInfo | null;
  consecutiveFailures: number;
  isRateLimited: boolean;
  baseUrl: string;
  initialized: boolean;

  // Actions
  setHealth: (health: HealthState, details?: PollStatusInfo) => void;
  setConnection: (state: ConnectionState) => void;
  setPolling: (isPolling: boolean) => void;
  incrementFailures: () => void;
  resetFailures: () => void;
  setRateLimited: (isRateLimited: boolean) => void;
  setBaseUrl: (url: string) => void;
  setInitialized: (initialized: boolean) => void;
  reset: () => void;
}

const initialState = {
  health: 'unhealthy' as HealthState,
  connection: 'disconnected' as ConnectionState,
  isPolling: false,
  lastPollTime: null,
  lastPollStatus: null,
  consecutiveFailures: 0,
  isRateLimited: false,
  baseUrl: '',
  initialized: false,
};

export const useGatewayStore = createStore<GatewayState>()(
  devtools(
    (set) => ({
      // Initial state
      ...initialState,

      // Actions
      setHealth: (health, details) =>
        set(
          (state) => ({
            health,
            lastPollStatus: details || state.lastPollStatus,
            lastPollTime: Date.now(),
            consecutiveFailures:
              health === 'healthy' ? 0 : state.consecutiveFailures,
          }),
          false,
          'setHealth'
        ),

      setConnection: (connection) =>
        set({ connection }, false, 'setConnection'),

      setPolling: (isPolling) => set({ isPolling }, false, 'setPolling'),

      incrementFailures: () =>
        set(
          (state) => ({
            consecutiveFailures: state.consecutiveFailures + 1,
          }),
          false,
          'incrementFailures'
        ),

      resetFailures: () =>
        set({ consecutiveFailures: 0 }, false, 'resetFailures'),

      setRateLimited: (isRateLimited) =>
        set({ isRateLimited }, false, 'setRateLimited'),

      setBaseUrl: (baseUrl) => set({ baseUrl }, false, 'setBaseUrl'),

      setInitialized: (initialized) =>
        set({ initialized }, false, 'setInitialized'),

      reset: () => set(initialState, false, 'reset'),
    }),
    { name: 'GatewayStore', enabled: !import.meta.env.PROD }
  )
);

// Export type for external use
export type { GatewayState };
